import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import trim from 'lodash/trim'
import toLower from 'lodash/toLower'
import keys from 'lodash/keys'
import { ExclamationCircleIcon } from '@heroicons/react/outline'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import notify from 'components/atoms/Notify'
import LoadingIcon from 'components/atoms/LoadingIcon'
import Switch from 'components/atoms/Switch'

const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const FieldError = ({ message }) => {
  if (!message) return null

  return (
    <p className="mt-2 text-sm text-red-600" role="alert">
      {message}
    </p>
  )
}

const AddEditNewsletterSubscriber = ({ editData = null, onSuccess, close }) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'

  const [email, setEmail] = useState(editData?.email || '')
  const [source, setSource] = useState(editData?.source || 'admin')
  const [active, setActive] = useState(editData ? editData.status === 'active' : true)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const clearError = field => {
    setErrors(prev => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const validateForm = () => {
    const nextErrors = {}
    const trimmedEmail = trim(email)

    if (!trimmedEmail) {
      nextErrors.email = t('common.required')
    } else if (!isValidEmail(toLower(trimmedEmail))) {
      nextErrors.email = t('newsletter.invalidEmail')
    }

    return nextErrors
  }

  const handleSubmit = async e => {
    e.preventDefault()
    const validationErrors = validateForm()
    if (keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setErrors({})
    setSubmitting(true)

    const payload = {
      email: toLower(trim(email)),
      source: trim(source) || 'admin',
      active,
    }

    const response = editData ? await apiPut(`newsletter-subscriptions/${editData._id}`, payload) : await apiPost('newsletter-subscriptions', payload, 0, false)

    setSubmitting(false)

    if (response?.success) {
      notify.success(editData ? t('newsletter.updateSuccess') : t('newsletter.createSuccess'))
      onSuccess?.()
      close?.()
      return
    }

    if (response?.message) {
      setErrors({ email: response.message })
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="space-y-6 sm:space-y-5">
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label htmlFor="newsletter-modal-email" className="block text-sm font-normal text-gray-700 mb-1">
            {t('newsletter.columns.email')}
          </label>
          <div className="relative rounded-md shadow-sm">
            <input
              id="newsletter-modal-email"
              type="text"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value)
                clearError('email')
              }}
              className={classNames(
                'block w-full sm:text-sm rounded-md border-gray-300 focus:outline-none',
                errors.email && 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 pr-10'
              )}
              placeholder="user@example.com"
              dir="ltr"
            />
            {errors.email && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
              </div>
            )}
          </div>
          <FieldError message={errors.email} />
        </div>

        <div>
          <label htmlFor="newsletter-modal-source" className="block text-sm font-normal text-gray-700 mb-1">
            {t('newsletter.columns.source')}
          </label>
          <input
            id="newsletter-modal-source"
            type="text"
            value={source}
            onChange={e => setSource(e.target.value)}
            className="block w-full sm:text-sm rounded-md border-gray-300 focus:outline-none"
            placeholder={t('newsletter.sourcePlaceholder')}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <label htmlFor="newsletter-modal-active" className="text-sm font-normal text-gray-700">
            {t('newsletter.columns.active')}
          </label>
          <div className="flex items-center gap-2">
            <Switch value={active} onChange={setActive} />
            <span className="text-sm text-gray-600">{active ? t('newsletter.active') : t('newsletter.inactive')}</span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          className="inline-flex justify-center rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200"
          onClick={close}
          disabled={submitting}
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm rounded-md text-white bg-dark-600 hover:bg-dark-700 disabled:opacity-60 min-w-[88px]"
        >
          {submitting && <LoadingIcon className="text-white" />}
          {editData ? t('common.update') : t('common.create')}
        </button>
      </div>
    </form>
  )
}

export default AddEditNewsletterSubscriber
